import React from "react";
import ReactECharts from "echarts-for-react";
import { EChartsOption, SeriesOption } from "echarts";

export interface LineData {
  name: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  textColor?: string;
  lineType?: "solid" | "dashed"; // Add line type as an optional property
}

export interface LineChartProps {
  labels: string[];
  dataSets: LineData[];
  xAxisTitle: string;
  yAxisTitle: string;
  verticalLineAt?: number; // Add optional verticalLineAt prop for vertical line
  highlightAreaFrom?: number;
  showSymbols?: boolean;
  height?: string;
  ref?: React.Ref<any> | null;
  yAxisMin?: number | "dataMin";
}

const LineChart: React.FC<LineChartProps> = ({
  labels,
  dataSets,
  xAxisTitle,
  yAxisTitle,
  verticalLineAt = 0, // Default to index 53 if not provided
  highlightAreaFrom,
  showSymbols = true,
  height = "400px",
  ref,
  yAxisMin = "dataMin",
}) => {
  const formatYAxisLabel = (value: number) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + "M";
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + "K";
    } else {
      return value.toFixed(1);
    }
  };

  // Formatter for x-axis
  const formatXAxisLabel = (label: string) => {
    const num = Number(label);
    if (!isNaN(num)) {
      return Math.round(num).toLocaleString(); // Format with comma separators
    }

    return label
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const formatAxisTitle = (title: string) => {
    return title
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word?.slice(1))
      .join(" ");
  };
  const formatLegendLabel = (label: string) => {
    return label
      ?.split(" ")
      ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      ?.join(" ");
  };

  const series: SeriesOption[] = dataSets.map((dataSet, index) => {
    return {
      name: formatLegendLabel(dataSet.name),
      type: "line",
      data: dataSet.data,
      lineStyle: {
        color: dataSet.textColor,
        type: dataSet.lineType || "solid",
        width: 3,
      },
      itemStyle: {
        color: dataSet.textColor,
      },
      areaStyle: {
        color: dataSet.textColor,
        opacity: 0.1,
      },
      symbol: showSymbols ? "circle" : "none",
      symbolSize: 4,
      label: {
        show: false,
      },
      markLine:
        verticalLineAt !== undefined
          ? {
              data: [
                {
                  xAxis: verticalLineAt,
                  lineStyle: {
                    type: "dashed",
                    color: dataSet.textColor,
                    width: 2,
                  },
                  label: {
                    show: false,
                  },
                },
              ],
              symbol: "none",
            }
          : undefined,
      markArea:
        highlightAreaFrom !== undefined
          ? {
              data: [
                [
                  {
                    xAxis: highlightAreaFrom,
                    itemStyle: {
                      color: "rgba(20, 57, 105, 0.1)",
                    },
                  },
                  {
                    xAxis: "max",
                  },
                ],
              ],
            }
          : undefined,
    };
  });

  const option: EChartsOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderColor: "#004aad",
      borderWidth: 1,
      textStyle: {
        color: "#004aad",
      },
      formatter: (params: any) => {
        let tooltipContent = `${params[0]?.axisValue}<br/>`;
        params.forEach((param: any) => {
          const value = Math.round(param?.value).toFixed(2);
          tooltipContent += `${param?.marker} ${param?.seriesName}: ${Number(
            value
          ).toFixed(2)}<br/>`;
        });
        return tooltipContent;
      },
    },
    legend: {
      data: dataSets.map((dataSet) => ({
        name: formatLegendLabel(dataSet.name),
        textStyle: {
          color: dataSet.textColor ?? "#9CA3AF", // fallback gray if color not available
          fontSize: 14,
          fontWeight: "bold",
        },
      })),
      top: "5%",
      left: "2%",
      right: "5%",
      // textStyle: {
      //   color: "blue",
      //   fontSize: 14,
      //   fontWeight: "bold",
      // },
    },
    grid: {
      left: "100px",
      right: "20px",
      bottom: "100px",
      top: "30%",
    },
    xAxis: {
      type: "category",
      data: labels,
      name: formatAxisTitle(xAxisTitle),
      nameLocation: "middle",
      nameGap: 70,
      nameTextStyle: {
        fontSize: 16,
        color: "black",
        fontWeight: "bold",
      },
      axisLabel: {
        formatter: (_value: string, index: number) =>
          index % 2 === 0 ? formatXAxisLabel(_value) : "",
        rotate: 0,
        fontSize: 14,
        color: "black",
        fontWeight: "bold",
        margin: 15,
      },
      axisLine: {
        lineStyle: {
          color: "#004aad",
          width: 2,
        },
      },
    },
    yAxis: {
      type: "value",
      name: formatAxisTitle(yAxisTitle),
      nameLocation: "middle",
      nameGap: 70,
      nameRotate: 90,
      nameTextStyle: {
        fontSize: 16,
        color: "black",
        fontWeight: "bold",
      },
      axisLabel: {
        formatter: (value: number) => formatYAxisLabel(value),
        fontSize: 14,
        color: "black",
        fontWeight: "bold",
      },
      axisLine: {
        lineStyle: {
          color: "black",
          width: 2,
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(20, 57, 105, 0.1)",
          type: "dashed",
        },
      },
      min: yAxisMin,
      max: "dataMax",
    },
    series,
  };

  return (
    <ReactECharts
      ref={ref}
      option={option}
      style={{ height: height, width: "100%" }}
    />
  );
};

export default LineChart;
